import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  Bell,
  Boxes,
  Building2,
  Check,
  ChevronDown,
  LogOut,
  Rocket,
  Settings,
  ShieldAlert,
  Sun,
  Moon,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';

import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth';
import { useOrganizations } from '@/lib/roles';
import { cn } from '@/lib/utils';

/** Application chrome: project-scoped navigation plus account controls. */
export function Layout() {
  const { projectId } = useParams();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
        {/* Wraps rather than squeezes: below desktop widths the project
            navigation drops to a row of its own under the controls. */}
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-4 px-4">
          <NavLink to="/" className="flex h-14 items-center gap-2 font-semibold">
            <span className="grid size-6 place-items-center rounded bg-primary text-primary-foreground">
              <AlertTriangle className="size-3.5" />
            </span>
            Outpost
          </NavLink>

          {projectId && (
            <nav className="order-last -mx-4 flex w-[calc(100%+2rem)] items-center gap-1 scrollbar-none overflow-x-auto border-t border-border px-4 py-1.5 lg:order-none lg:mx-0 lg:w-auto lg:border-t-0 lg:p-0">
              <NavItem to={`/projects/${projectId}/issues`} icon={<AlertTriangle />}>
                Issues
              </NavItem>
              <NavItem to={`/projects/${projectId}/releases`} icon={<Rocket />}>
                Releases
              </NavItem>
              <NavItem to={`/projects/${projectId}/alerts`} icon={<Bell />}>
                Alerts
              </NavItem>
              <NavItem to={`/projects/${projectId}/settings`} icon={<Settings />}>
                Settings
              </NavItem>
            </nav>
          )}

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="sm" asChild>
              <NavLink to="/" title="Projects">
                <Boxes />
                <span className="hidden sm:inline">Projects</span>
              </NavLink>
            </Button>
            <OrganizationMenu />
            <ThemeToggle />
            <AccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full min-w-0 max-w-[1400px] flex-1 px-4 py-5 sm:py-6">
        <TwoFactorNotice />
        {projectId && <ArchivedNotice projectId={projectId} />}
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

/**
 * Shown when an organization the user belongs to requires a second factor and
 * this session was not established with one.
 *
 * Without it their projects simply disappear from the list, which looks like
 * having been removed from the organization rather than like something they can
 * fix in a minute.
 */
function TwoFactorNotice() {
  const { mfa } = useAuth();
  const organizations = useOrganizations();

  if (mfa) return null;
  const waiting = organizations.data?.filter((organization) => organization.require_mfa) ?? [];
  if (waiting.length === 0) return null;

  const names = waiting.map((organization) => organization.name).join(', ');

  return (
    <div className="mb-5 flex items-start gap-2 rounded-lg bg-caution-subtle px-3 py-2 text-sm text-caution-strong">
      <ShieldAlert className="mt-0.5 size-4 shrink-0" />
      <span>
        {names} {waiting.length === 1 ? 'requires' : 'require'} two-factor authentication, and you
        signed in with only a password. Add an authenticator app or a passkey in{' '}
        <Link to="/account" className="underline underline-offset-4">
          your user settings
        </Link>
        , or sign in again using one you already have.
      </span>
    </div>
  );
}

/**
 * Said on every page of an archived project: an issue stream that has gone
 * quiet looks exactly like one that is healthy.
 */
function ArchivedNotice({ projectId }: { projectId: string }) {
  const { client } = useAuth();
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => client.getProject(projectId),
  });

  if (project.data?.is_active !== false) return null;

  return (
    <div className="mb-5 flex items-start gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
      <Archive className="mt-0.5 size-4 shrink-0" />
      This project is archived. It no longer accepts events, and everything it recorded is still
      here.
    </div>
  );
}

/**
 * The way into each organization's settings.
 *
 * Kept apart from the account menu: organization settings are shared, and
 * reaching them from a personal menu suggests otherwise. The trigger names the
 * organization of whatever is on screen, so that on a project page it is clear
 * whose settings are one click away.
 */
function OrganizationMenu() {
  const { projectId, orgId, teamId } = useParams();
  const { client } = useAuth();
  const organizations = useOrganizations();

  // Both are already cached by the pages that put these ids in the URL.
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => client.getProject(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  const team = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => client.getTeam(teamId ?? ''),
    enabled: Boolean(teamId),
  });

  const list = organizations.data ?? [];
  if (list.length === 0) return null;

  const currentId =
    orgId ??
    (projectId ? project.data?.org_id : teamId ? team.data?.org_id : undefined) ??
    (list.length === 1 ? list[0]?.id : undefined);
  const current = list.find((organization) => organization.id === currentId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" title="Organization settings" className="gap-1.5">
          <Building2 />
          <span className="hidden max-w-40 truncate sm:inline">
            {current?.name ?? 'Organizations'}
          </span>
          <ChevronDown className="hidden sm:block" />
          <span className="sr-only sm:hidden">Organization settings</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-w-[calc(100vw-2rem)] [&_svg]:size-4">
        <DropdownMenuLabel>Organization settings</DropdownMenuLabel>
        {list.map((organization) => (
          <DropdownMenuItem key={organization.id} asChild>
            <Link to={`/organizations/${organization.id}/settings`}>
              <Building2 className="text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{organization.name}</span>
              {organization.id === current?.id ? (
                <Check />
              ) : (
                <span className="text-xs text-muted-foreground">{organization.role}</span>
              )}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The signed-in person's own settings and signing out. */
function AccountMenu() {
  const { user, logout } = useAuth();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" title="Account">
          <UserRound />
          <span className="hidden max-w-48 truncate xl:inline">{user?.email}</span>
          <ChevronDown className="hidden sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-[calc(100vw-2rem)] [&_svg]:size-4">
        <DropdownMenuLabel>
          <span className="block truncate text-sm text-foreground">{user?.name}</span>
          <span className="block truncate text-xs font-normal text-muted-foreground">
            {user?.email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/account">
            <Settings />
            User settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          // Spread across its own row below desktop widths, and without icons
          // on a phone, which only has room for the words.
          'inline-flex flex-1 shrink-0 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm transition-colors lg:flex-none [&_svg]:hidden sm:[&_svg]:block [&_svg]:size-3.5',
          isActive
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

/** Light by default; the preference is remembered across sessions. */
function ThemeToggle() {
  const [dark, setDark] = useState(() => localStorage.getItem('outpost.theme') === 'dark');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.classList.toggle('light', !dark);
    localStorage.setItem('outpost.theme', dark ? 'dark' : 'light');
  }, [dark]);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setDark((value) => !value)}
      title="Toggle theme"
    >
      {dark ? <Sun /> : <Moon />}
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}
